"use client";

import Link from "next/link";
import { useMemo, useState } from "react";

type Pattern = {
  name: string;
  description: string;
};

type Answers = Record<string, number>;

const questions = [
  {
    text: "I crash between 2PM and 4PM",
    category: "blood_sugar",
    weight: 3,
  },
  {
    text: "I feel bloated regularly",
    category: "gut",
    weight: 3,
  },
  {
    text: "I rely on caffeine to function",
    category: "blood_sugar",
    weight: 2,
  },
  {
    text: "I feel wired at night but drained during the day",
    category: "burnout",
    weight: 3,
  },
  {
    text: "I crave sugar or carbs most afternoons",
    category: "blood_sugar",
    weight: 3,
  },
  {
    text: "I feel foggy, flat, or mentally slow",
    category: "burnout",
    weight: 2,
  },
  {
    text: "My stomach often feels unsettled after meals",
    category: "gut",
    weight: 3,
  },
  {
    text: "I wake up tired even after 7–8 hours of sleep",
    category: "burnout",
    weight: 3,
  },
  {
    text: "I get irritable or shaky if I go too long without eating",
    category: "blood_sugar",
    weight: 3,
  },
  {
    text: "My energy swings up and down through the day",
    category: "blood_sugar",
    weight: 2,
  },
  {
    text: "I struggle to concentrate for long periods",
    category: "burnout",
    weight: 2,
  },
  {
    text: "I feel heavy, puffy, or inflamed more often than I should",
    category: "gut",
    weight: 2,
  },
] as const;

const patterns: Pattern[] = [
  {
    name: "Blood Sugar Rollercoaster",
    description:
      "You are likely getting short bursts of energy followed by crashes, cravings, and inconsistent focus.",
  },
  {
    name: "Burnout Mode",
    description:
      "Your body may be leaning too heavily on stress hormones, which can leave you tired-but-wired and mentally drained.",
  },
  {
    name: "Gut Drain",
    description:
      "Low-grade inflammation and digestive stress may be quietly stealing your energy and clouding your thinking.",
  },
  {
    name: "Stable But Fragile",
    description:
      "You are not far off, but a few daily habits may be keeping your energy from feeling calm, steady, and reliable.",
  },
];

const faqs = [
  {
    q: "What happens after I take the scorecard?",
    a: "You'll see which energy pattern is most likely driving your fatigue and get practical next-step guidance by email.",
  },
  {
    q: "Is this medical advice?",
    a: "No. This is an educational self-assessment designed to help you spot common patterns behind fatigue, crashes, cravings, bloating, and brain fog.",
  },
  {
    q: "How long does it take?",
    a: "About 2 minutes.",
  },
] as const;

export default function EnergyScorecardClient() {
  const [answers, setAnswers] = useState<Answers>({});
  const [name, setName] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [result, setResult] = useState<Pattern | null>(null);
  const [error, setError] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const totalAnswered = useMemo(() => Object.keys(answers).length, [answers]);

  const handleSelect = (questionKey: string, value: number) => {
    if (submitted) return;
    setAnswers((prev) => ({ ...prev, [questionKey]: value }));
  };

  const calculateResult = (): Pattern => {
    const scores = {
      blood_sugar: 0,
      burnout: 0,
      gut: 0,
    };

    questions.forEach((question) => {
      const key = question.text.toLowerCase().replace(/[^a-z0-9]+/g, "-");
      const answer = answers[key] ?? 0;
      scores[question.category] += answer * question.weight;
    });

    const ranked = Object.entries(scores).sort((a, b) => b[1] - a[1]);
    const [topCategory, topScore] = ranked[0];
    const [, secondScore] = ranked[1];

    if (topScore <= 8 || topScore - secondScore <= 2) {
      return patterns[3];
    }

    if (topCategory === "blood_sugar") return patterns[0];
    if (topCategory === "burnout") return patterns[1];
    if (topCategory === "gut") return patterns[2];

    return patterns[3];
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!name.trim() || !email.trim()) {
      setError("Please enter your name and email.");
      return;
    }

    if (totalAnswered === 0) {
      setError("Please answer at least one question.");
      return;
    }

    setError("");
    setIsSubmitting(true);

    const calculated = calculateResult();

    try {
      const res = await fetch("/api/energy/scorecard", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name,
          email,
          answers,
          resultPattern: calculated.name,
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.message || "Something went wrong. Please try again.");
        setIsSubmitting(false);
        return;
      }
    } catch {
      setError("Could not save your results. Please try again.");
      setIsSubmitting(false);
      return;
    }

    setResult(calculated);
    setSubmitted(true);
    setIsSubmitting(false);
  };

  return (
    <main className="bg-[var(--bg-page)] overflow-x-hidden">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 bg-[var(--bg-page)] text-[var(--text-on-dark)] px-3 py-2 rounded-md z-50"
      >
        Skip to content
      </a>

      <header
        id="main-content"
        className="relative max-w-[1180px] mx-auto px-6 pt-16 sm:pt-20 pb-14 grid lg:grid-cols-[1.1fr_0.9fr] gap-10 items-start"
      >
        <div>
          <p className="flex items-center gap-3 text-[0.75rem] font-medium tracking-[0.1em] uppercase text-[var(--accent)] mb-6">
            <span className="block w-7 h-px bg-[var(--accent)]" />
            Free 2-minute energy assessment
          </p>

          <h1 className="font-serif text-[clamp(2.4rem,5vw,4.4rem)] font-light leading-[1.08] text-[var(--text-on-dark)] mb-6">
            You're not lazy.
            <br />
            You're running on <em className="italic text-[var(--accent)]">empty.</em>
          </h1>

          <p className="text-[1.02rem] text-[var(--text-on-dark-muted)] leading-[1.8] mb-8 max-w-[560px]">
            If you feel tired all the time, crash at 3PM, or can't think clearly without coffee,
            this scorecard will help you understand what pattern may be driving it.
          </p>

          <div className="grid sm:grid-cols-3 gap-3 mb-8 max-w-[680px]">
            {[
              "Find out why your energy feels off",
              "See the pattern behind your crashes, cravings, or brain fog",
              "Get a practical next step instead of more guesswork",
            ].map((item) => (
              <div
                key={item}
                className="rounded-2xl p-4 border"
                style={{
                  background: "var(--bg-glass-dark)",
                  borderColor: "var(--border-on-dark)",
                }}
              >
                <p className="text-[0.88rem] leading-[1.6] text-[var(--text-on-dark-muted)]">
                  {item}
                </p>
              </div>
            ))}
          </div>

          <div className="flex flex-col items-start gap-3">
            <a
              href="#scorecard"
              className="rounded-full px-8 py-4 font-medium transition-all hover:-translate-y-0.5 bg-[var(--cream)] text-[var(--ink)]"
            >
              Take the free scorecard
            </a>
            <p className="text-[0.8rem] italic text-[var(--text-on-dark-faint)]">
              Takes 2 minutes · Free · Instant clarity
            </p>
          </div>
        </div>

        <aside
          className="rounded-[28px] border p-6 lg:mt-6"
          style={{
            background: "var(--bg-surface)",
            borderColor: "var(--border-on-light)",
          }}
        >
          <p className="text-[0.72rem] font-medium tracking-[0.1em] uppercase mb-3 text-[var(--text-brand)]">
            What this uncovers
          </p>
          <h2 className="font-serif text-[1.9rem] font-light leading-[1.2] mb-4 text-[var(--text-on-light)]">
            Why coffee isn't working anymore
          </h2>
          <ul className="space-y-3 text-[0.95rem] leading-[1.7] text-[var(--text-on-light-muted)]">
            <li>• Blood sugar swings that create energy crashes</li>
            <li>• Gut stress that can show up as bloating and brain fog</li>
            <li>• "Healthy" habits that may be making you feel worse</li>
            <li>• Signs your metabolism is dysregulated — but fixable</li>
          </ul>
        </aside>
      </header>

      <section className="bg-[var(--ink)] py-4 px-6">
        <ul className="max-w-[1100px] mx-auto flex flex-wrap items-center justify-center gap-x-8 gap-y-2 list-none">
          {[
            "Afternoon crashes",
            "Brain fog",
            "Cravings",
            "Bloating",
            "Low motivation",
            "Coffee dependence",
          ].map((item) => (
            <li
              key={item}
              className="text-[0.8rem] tracking-wide text-[var(--sand)]"
            >
              {item}
            </li>
          ))}
        </ul>
      </section>

      <section className="bg-[var(--bg-surface)] py-20 px-6">
        <div className="max-w-[860px] mx-auto text-center">
          <blockquote className="font-serif text-[clamp(1.5rem,3vw,2.2rem)] font-light italic leading-[1.35] mb-6 text-[var(--text-on-light)]">
            "Most people with low energy don't need more discipline. They need the right explanation for what their body is doing."
          </blockquote>
          <p className="max-w-[620px] mx-auto text-[var(--text-on-light-muted)] leading-[1.8]">
            This isn't about trying harder. It's about understanding whether you're stuck in a blood sugar pattern, a stress pattern, a gut pattern, or a fragile balance that keeps tipping you back into exhaustion.
          </p>
        </div>
      </section>

      <section className="py-20 px-6 bg-[var(--bg-page)]">
        <div className="max-w-[1100px] mx-auto grid lg:grid-cols-2 gap-8 items-start">
          <div>
            <p className="flex items-center gap-3 text-[0.75rem] font-medium tracking-[0.1em] uppercase text-[var(--accent)] mb-4">
              <span className="block w-7 h-px bg-[var(--accent)]" />
              The 4 most common energy patterns
            </p>
            <h2 className="font-serif text-[clamp(2rem,4vw,2.9rem)] font-light leading-[1.18] text-[var(--text-on-dark)] mb-5">
              Tired is not a personality trait.
            </h2>
            <p className="text-[1rem] leading-[1.8] text-[var(--text-on-dark-muted)] max-w-[560px] mb-8">
              Most people are not dealing with "just low energy." They are repeating a specific pattern. Once you can see the pattern, you can stop guessing and start fixing the right thing first.
            </p>

            <div className="space-y-4">
              {patterns.map((pattern) => (
                <div
                  key={pattern.name}
                  className="rounded-2xl border p-5"
                  style={{
                    background: "var(--bg-glass-dark)",
                    borderColor: "var(--border-on-dark)",
                  }}
                >
                  <h3 className="font-serif text-[1.2rem] mb-2 text-[var(--text-on-dark)]">
                    {pattern.name}
                  </h3>
                  <p className="text-[0.92rem] leading-[1.7] text-[var(--text-on-dark-muted)]">
                    {pattern.description}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div
            id="scorecard"
            className="rounded-[30px] border p-5 sm:p-6 md:p-8 lg:sticky lg:top-6"
            style={{
              background: "var(--bg-surface)",
              borderColor: "var(--border-on-light)",
            }}
          >
            <p className="text-[0.72rem] font-medium tracking-[0.1em] uppercase mb-3 text-[var(--text-brand)]">
              Energy Reset Scorecard
            </p>
            <h2 className="font-serif text-[2rem] font-light leading-[1.15] mb-3 text-[var(--text-on-light)]">
              Find your pattern in 2 minutes
            </h2>
            <p className="text-[0.95rem] leading-[1.7] mb-6 text-[var(--text-on-light-muted)]">
              Score each statement from 0 to 3.
              <br />
              0 = never · 1 = sometimes · 2 = often · 3 = almost always
            </p>

            <form className="space-y-6" onSubmit={handleSubmit}>
              {questions.map((question) => {
                const slug = question.text.toLowerCase().replace(/[^a-z0-9]+/g, "-");
                const selectedValue = answers[slug];

                return (
                  <div
                    key={question.text}
                    className="rounded-2xl bg-[var(--bg-brand-tint)] p-4"
                  >
                    <p className="text-[0.9rem] leading-[1.6] mb-3 text-[var(--text-on-light)]">
                      {question.text}
                    </p>

                    <div className="grid grid-cols-4 gap-2 sm:gap-2.5">
                      {[0, 1, 2, 3].map((value) => {
                        const isSelected = selectedValue === value;

                        return (
                          <label
                            key={value}
                            htmlFor={`${slug}-${value}`}
                            className={`rounded-xl border px-3 py-3 sm:py-2 text-center text-[0.9rem] sm:text-[0.82rem] cursor-pointer transition-all duration-150 select-none ${
                              isSelected ? "font-semibold shadow-md" : "hover:bg-white/40"
                            } ${submitted ? "pointer-events-none opacity-70" : ""}`}
                            style={{
                              borderColor: isSelected
                                ? "var(--text-brand)"
                                : "var(--border-on-light)",
                              background: isSelected ? "var(--text-brand)" : "var(--bg-card)",
                              color: isSelected ? "var(--btn-anchor-text)" : "var(--text-on-light-muted)",
                              boxShadow: isSelected
                                ? "0 6px 18px color-mix(in srgb, var(--burgundy) 18%, transparent)"
                                : "none",
                            }}
                          >
                            <input
                              id={`${slug}-${value}`}
                              name={slug}
                              type="radio"
                              value={value}
                              checked={selectedValue === value}
                              onChange={() => handleSelect(slug, value)}
                              className="sr-only"
                              disabled={submitted}
                            />
                            <span
                              style={{
                                color: isSelected ? "var(--btn-anchor-text)" : "inherit",
                                display: "block",
                                lineHeight: 1,
                              }}
                            >
                              {value}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                );
              })}

              <div className="rounded-2xl bg-[color-mix(in_srgb,var(--burgundy)_6%,transparent)] p-5">
                <h3 className="font-serif text-[1.2rem] mb-3 text-[var(--text-on-light)]">
                  Get your result
                </h3>
                <p className="text-[0.9rem] leading-[1.7] mb-4 text-[var(--text-on-light-muted)]">
                  Enter your details and I'll send your score, your likely energy pattern, and the first shift to make.
                </p>

                <div className="grid gap-3">
                  <input
                    type="text"
                    placeholder="First name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    disabled={submitted}
                    className="rounded-xl border px-4 py-3 bg-white text-[var(--text-on-light)] placeholder:text-[var(--text-on-light-muted)] disabled:opacity-60"
                    style={{ borderColor: "var(--border-on-light)" }}
                  />
                  <input
                    type="email"
                    placeholder="Email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={submitted}
                    className="rounded-xl border px-4 py-3 bg-white text-[var(--text-on-light)] placeholder:text-[var(--text-on-light-muted)] disabled:opacity-60"
                    style={{ borderColor: "var(--border-on-light)" }}
                  />
                  {!submitted && (
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="rounded-full px-6 py-4 font-medium transition-all hover:-translate-y-0.5 bg-[var(--text-brand)] text-[var(--btn-anchor-text)] disabled:opacity-60 disabled:cursor-not-allowed disabled:translate-y-0"
                    >
                      {isSubmitting ? "Saving your results…" : "See my energy pattern"}
                    </button>
                  )}
                </div>

                {error ? <p className="text-[0.8rem] mt-3 text-red-600">{error}</p> : null}

                {!submitted && (
                  <p className="text-[0.76rem] italic mt-3 text-[var(--text-on-light-faint)]">
                    By signing up, you'll also receive follow-up guidance on improving your energy.
                  </p>
                )}
              </div>

              {result ? (() => {
                const steps = {
                  "Blood Sugar Rollercoaster": {
                    tip: "Start your day with protein/fibre (eggs, yogurt, salad or similar) and avoid using coffee as your first fuel source.",
                  },
                  "Burnout Mode": {
                    tip: "Reduce stimulation in the evening (screens, caffeine) and focus on consistent sleep/wake timing.",
                  },
                  "Gut Drain": {
                    tip: "Simplify meals for a few days (whole foods, fewer ingredients) and notice how digestion responds.",
                  },
                  "Stable But Fragile": {
                    tip: "Keep your routine consistent — small daily habits will stabilise your energy long-term.",
                  },
                } as const;

                const current = steps[result.name as keyof typeof steps];

                return (
                  <div className="space-y-5">
                    {/* RESULT */}
                    <div className="mt-6 p-5 rounded-2xl bg-[color-mix(in_srgb,var(--burgundy)_8%,transparent)] border border-[var(--border-on-light)]">
                      <h4 className="font-serif text-[1.25rem] text-[var(--text-on-light)] mb-2">
                        Your Result: {result.name}
                      </h4>
                      <p className="text-[0.95rem] leading-[1.7] text-[var(--text-on-light-muted)]">
                        {result.description}
                      </p>
                      <p className="text-[0.78rem] mt-3 italic text-[var(--text-on-light-faint)]">
                        Full breakdown sent to your email.
                      </p>
                    </div>

                    {/* DYNAMIC STEP */}
                    <div
                      className="rounded-2xl border p-5"
                      style={{
                        background: "var(--bg-brand-tint)",
                        borderColor: "var(--border-on-light)",
                      }}
                    >
                      <p className="text-[0.72rem] font-medium tracking-[0.1em] uppercase mb-3 text-[var(--text-brand)]">
                        What to do next
                      </p>
                      <h5 className="font-serif text-[1.3rem] text-[var(--text-on-light)] mb-3">
                        Start here first
                      </h5>
                      <p className="text-[0.92rem] leading-[1.75] text-[var(--text-on-light-muted)]">
                        {current?.tip}
                      </p>
                    </div>

                    {/* CTA BLOCKS */}
                    <div className="grid gap-4">
                      <div
                        className="rounded-2xl border p-5"
                        style={{
                          background: "var(--bg-brand-tint)",
                          borderColor: "var(--border-on-light)",
                        }}
                      >
                        <h5 className="font-serif text-[1.15rem] text-[var(--text-on-light)] mb-2">
                          Want help understanding your best starting point?
                        </h5>
                        <p className="text-[0.92rem] leading-[1.75] mb-4 text-[var(--text-on-light-muted)]">
                          If you're not sure what's actually driving your fatigue, a short conversation is the easiest place to start.
                        </p>
                        <Link
                          href="/workwithme/discoverycall"
                          className="inline-flex rounded-full px-6 py-3 font-medium transition-all hover:-translate-y-0.5 bg-[var(--text-brand)] text-[var(--btn-anchor-text)]"
                        >
                          Book a free discovery call
                        </Link>
                      </div>

                      <div
                        className="rounded-2xl border p-5"
                        style={{
                          background: "var(--bg-brand-tint)",
                          borderColor: "var(--border-on-light)",
                        }}
                      >
                        <h5 className="font-serif text-[1.15rem] text-[var(--text-on-light)] mb-2">
                          Ready for a more structured reset?
                        </h5>
                        <p className="text-[0.92rem] leading-[1.75] mb-4 text-[var(--text-on-light-muted)]">
                          The Farleigh Method is my signature programme for helping you fix this properly.
                        </p>
                        <Link
                          href="/workwithme/farleighMethod"
                          className="inline-flex rounded-full px-6 py-3 font-medium transition-all hover:-translate-y-0.5 border border-[var(--text-brand)] text-[var(--text-brand)]"
                        >
                          Explore the Farleigh Method
                        </Link>
                      </div>
                    </div>

                    <p className="text-[0.9rem] leading-[1.7] text-[var(--text-on-light-muted)]">
                      You don't need more willpower. You need the right explanation and the right starting point.
                    </p>
                  </div>
                );
              })() : null}
            </form>
          </div>
        </div>
      </section>

      <section className="py-20 px-6 bg-[var(--bg-surface)]">
        <div className="max-w-[980px] mx-auto text-center mb-12">
          <h2 className="font-serif text-[clamp(1.9rem,4vw,2.8rem)] font-light text-[var(--text-on-light)]">
            Relief first. Then a clear next step.
          </h2>
        </div>

        <div className="max-w-[980px] mx-auto grid md:grid-cols-3 gap-5">
          {[
            {
              title: "A better explanation",
              body: "You'll understand why your energy feels unstable instead of assuming you just need more sleep or more willpower.",
            },
            {
              title: "A named pattern",
              body: "You'll see which pattern fits you most closely so your fatigue starts to make sense.",
            },
            {
              title: "A first move",
              body: "You'll get one practical next step to help you steady energy, reduce crashes, and think more clearly.",
            },
          ].map((item) => (
            <div
              key={item.title}
              className="rounded-2xl border p-7"
              style={{
                background: "var(--bg-surface-dk)",
                borderColor: "var(--border-on-light)",
              }}
            >
              <h3 className="font-serif text-[1.25rem] mb-3 text-[var(--text-on-light)]">
                {item.title}
              </h3>
              <p className="text-[0.92rem] leading-[1.75] text-[var(--text-on-light-muted)]">
                {item.body}
              </p>
            </div>
          ))}
        </div>
      </section>

      <section className="py-20 px-6 bg-[var(--bg-page)]">
        <div className="max-w-[760px] mx-auto">
          <div className="text-center mb-10">
            <h2 className="font-serif text-[clamp(1.7rem,3vw,2.4rem)] font-light text-[var(--text-on-dark)]">
              Frequently asked questions
            </h2>
          </div>

          <div className="space-y-4">
            {faqs.map((faq) => (
              <details
                key={faq.q}
                className="rounded-2xl border p-5"
                style={{
                  background: "var(--bg-glass-dark)",
                  borderColor: "var(--border-on-dark)",
                }}
              >
                <summary className="cursor-pointer list-none font-medium text-[var(--text-on-dark)]">
                  {faq.q}
                </summary>
                <p className="mt-3 text-[0.92rem] leading-[1.75] text-[var(--text-on-dark-muted)]">
                  {faq.a}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section
        className="py-24 px-6 relative overflow-hidden"
        style={{ background: "var(--ink)" }}
      >
        <div className="max-w-[740px] mx-auto text-center relative">
          <h2 className="font-serif text-[clamp(2rem,4.5vw,3.2rem)] font-light leading-[1.2] mb-5 text-[var(--cream)]">
            Stop guessing why you're tired.
          </h2>

          <p className="max-w-[520px] mx-auto mb-10 text-[var(--sand)]">
            Take the Energy Reset Scorecard and find the pattern behind your crashes, cravings, bloating, and brain fog.
          </p>

          <div className="flex flex-wrap gap-4 justify-center">
            <a
              href="#scorecard"
              className="rounded-full px-10 py-4 font-medium transition-all hover:-translate-y-0.5 bg-[var(--cream)] text-[var(--ink)]"
            >
              Take the scorecard now
            </a>
            <Link
              href="/"
              className="rounded-full px-10 py-4 font-medium border border-white/20 text-[var(--cream)]"
            >
              Back to homepage
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
